import sections from '../../content/sections.json'
import { BRANCHES } from './branches.js'

// The places a heartbeat may name — ONE derivation, imported by both ends.
// P6-242b, closing the gap between ADR-0010 D1's letter and its intent.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHY THIS IS A MODULE BOTH ENDS IMPORT, AND WHAT IT IS DERIVED FROM
// ═══════════════════════════════════════════════════════════════════════════
//
// ADR-0010's intent is that a member anywhere on the signed-in site is PRESENT,
// and that a nav pill shows a dot when somebody is on it. Its letter — *"`home`
// plus the pill ids in `branches.js`"* — described the portal's own pills and
// missed the top nav, which is where the dots are drawn. So a member reading
// /docs, /commands or /releases was refused by the validator, and those three
// pills could never light.
//
// The set is therefore THREE things, and each is DERIVED, never typed:
//
//   1. `home`                         the portal's front page;
//   2. every id in `BRANCHES`         the portal's pills (`branches.js`, which
//                                     imports nothing);
//   3. every top-level registry       the sections the header's nav draws a
//      section                        pill and a tab for.
//
// (3) is read from `content/sections.json`, the file `content.js` builds
// `topLevelSections` from, with the rule `content.js` documents for it — "the
// nav is top-level only", a section with no `parent`. It is NOT imported from
// `content.js`: that would carry the whole command catalogue and every docs page
// into a Pages Function that wants one 3 KB registry. `functions/_lib/guard.js`
// already imports the same generated file for the same reason.
//
// ⚠️ THAT IS A SECOND COPY OF ONE FILTER, SAID OUT LOUD. Two expressions of
// "which sections does the nav draw" can only disagree, and the failure would be
// the worst kind — a pill added to the nav whose heartbeats are refused, silently,
// because the browser ignores a failed beat by design (gotcha 21). So the copy is
// pinned against its original: `src/test/portal/presence.test.jsx` asserts this
// module's nav sections equal `topLevelSections`, and asserts that every pill the
// RENDERED header draws maps to a path the Function accepts, and every accepted
// path to a pill. A pill without a path, or a path without a pill, fails there.
//
// The Function applies the predicate on WRITE (a heartbeat is refused) and on READ
// (an entry the service hands back is dropped), and the client sends only what it
// accepts (`presence.js::presencePathFor`) — three users, one definition.

/** The section a member is on when they are on the portal's front page. */
export const HOME_PATH = 'home'

/** The registry's top-level sections: the ones the header's nav and tab bar draw.
 * Same file and same rule as `content.js::topLevelSections`. */
export const NAV_SECTIONS = sections.sections.filter((section) => !section.parent)

/**
 * The `path` a section's pages send, and the pill that path lights.
 *
 * The section whose route is `/` is the portal home and is called `home`; every
 * other top-level section is called by its registry id (`docs`, `commands`,
 * `trips`, `releases`). Keyed on the ROUTE rather than on the id `landing`, so
 * renaming the registry's id for the front page cannot quietly stop `/` sending.
 */
export function presencePathOf(section) {
  return section.path === '/' ? HOME_PATH : section.id
}

/** Every top-level section's path, in nav order. */
export const NAV_PATHS = NAV_SECTIONS.map(presencePathOf)

/** The pill ids on the portal's front page (`movie`, `watch`, `music`, `trips`, …). */
export const PILL_PATHS = BRANCHES.map((branch) => branch.id)

/**
 * Everything a heartbeat may name. A Set, so membership can never be answered by
 * an inherited property (`constructor`, `toString`) the way an object lookup can.
 */
export const PRESENCE_PATHS = new Set([HOME_PATH, ...PILL_PATHS, ...NAV_PATHS])

/** Is this a place a heartbeat may name? Anything that is not a string is not. */
export const isPresencePath = (path) => typeof path === 'string' && PRESENCE_PATHS.has(path)
